import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({ standalone: false,
  selector: 'two-factor-login',
  templateUrl: './two-factor-login.component.html',
  styleUrls: ['./two-factor-login.component.css']
})
export class TwoFactorLoginComponent implements OnDestroy{
  @ViewChildren('F') inputs!: QueryList<ElementRef<HTMLInputElement>>;
  @Output() otpComplete = new EventEmitter<string>();
  @Output() setErrorMessage = new EventEmitter<string>();
  @Output() clearMessage = new EventEmitter<void>();
  @Output() goBackMessage = new EventEmitter<void>();

  otpValueRecord: string = "";
  otpForm: FormGroup;
  isSubmitting = false;

  otpSubscription: Subscription;

  constructor(private fb: FormBuilder) {
    this.otpForm = this.fb.group({
      d0: new FormControl(''),
      d1: new FormControl(''),
      d2: new FormControl(''),
      d3: new FormControl(''),
      d4: new FormControl(''),
      d5: new FormControl('')
    });

    this.otpSubscription = this.otpForm.valueChanges.subscribe(() => {
      if (this.digitsLeft === 0 && !this.isSubmitting) {
        this.isSubmitting = true;
        this.submit();
      }
      if (this.otpValueRecord != this.otpValue) {
        this.isSubmitting = false;
        this.otpValueRecord = this.otpValue;
      }
    });
  }

  ngOnDestroy(): void {
   this.otpSubscription.unsubscribe();
  }

  ngAfterViewInit() {
    // Auto focus first input
    setTimeout(() => {
      this.focus(0);
    }, 420);
  }

  get digitsLeft(): number {
    const filled = Object.values(this.otpForm.value)
      .filter(v => v && v.toString().length === 1).length;

    return 6 - filled;
  }

  get otpValue(): string {
    return Object.values(this.otpForm.value).join('');
  }

  // 🔥 MAIN KEY HANDLER (production safe)
  onKeyDown(event: KeyboardEvent, index: number) {
    const key = event.key;

    if (event.ctrlKey || event.metaKey) {
      return;
    }
    // Allow tab
    if (key === 'Tab')
      return;

    // Arrow navigation
    if (key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      this.focus(index + 1);
      return;
    }

    if (key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focus(index - 1);
      return;
    }

    // Backspace handling
    if (key === 'Backspace' || key === 'Delete') {
      event.preventDefault();

      const control = this.getControl(index);

      if (control.value) {
        control.setValue('');
      } else if (index > 0) {
        this.focus(index - 1);
        this.getControl(index - 1).setValue('');
      }
      return;
    }

    // Only allow digits
    if (!/^\d$/.test(key)) {
      event.preventDefault();
      return;
    }

    // Overwrite behavior
    event.preventDefault();
    this.getControl(index).setValue(key);

    if (index < 5) {
      this.focus(index + 1);
    }
  }

  onInput(event: any, index: number) {
    let value = event.target.value.replace(/\D/g, ''); // Remove non-digit characters
    let maxLength = 6 - index; // if they've clicked/pasted into the middle, we need to adjust the max length to prevent overflow
    event.target.value = value;
  
    if (value.length !== maxLength && value.length > 0) {
      this.setErrorMessage.emit('Invalid OTP format. Please enter a 6-digit code.');
      const control = this.getControl(index);
      control.setValue('');
      return;
    }
  
    const digits = value.split('');
    for (let i = 0; i < digits.length; i++) {
      const digit = digits[i];
      const control = this.getControl(i+index);
      if (control) {
        control.setValue(digit);
      }
    }
    
  }

  private getControl(index: number): FormControl {
    return this.otpForm.get(`d${index}`) as FormControl;
  }

  focus(index: number) {
    this.inputs.toArray()[index].nativeElement.focus();
  }

  private submit() {
    this.otpComplete.emit(this.otpValue);
  }

  goBack() {
    this.goBackMessage.emit()
    this.clearMessage.emit();
  }
}
